/**
 * Creative Studio page - AI-powered image generation and editing.
 *
 * Features:
 * - Generate HD ad images using Gemini 3 Pro
 * - Multi-turn iterative refinement with base image context
 * - Reference image support
 * - Save to Media Library
 *
 * This feature is currently in beta and only available to specific members.
 * Access is controlled by the CREATIVE_STUDIO_BETA_MEMBER_UUIDS list.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { hasCreativeStudioAccess } from '@/config/featureAccess';
import { useCreativeStudioStore, useSelectedImage } from '@/store/creativeStudioStore';
import { useStartRefinementSession } from '@/hooks/queries/useCreativeStudioQueries';
import { GenerationPanel } from './components/GenerationPanel';
import { RefinementPanel } from './components/RefinementPanel';
import { ImagePreview } from './components/ImagePreview';
import { SaveToMediaLibraryModal } from './components/SaveToMediaLibraryModal';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { AISparkleIcon } from '@/components/ui/Icons';
import toast from 'react-hot-toast';

const REFINE_FROM_LIBRARY_PARAM = 'refine_from_library';

export function CreativeStudio() {
  const { memberUuid } = useAuth();

  const {
    currentJobId,
    refinementSessionId,
    activeTab,
    setActiveTab,
    startRefinementSession,
  } = useCreativeStudioStore();

  const selectedImage = useSelectedImage();
  const startRefinementMutation = useStartRefinementSession();
  const [searchParams, setSearchParams] = useSearchParams();
  // StrictMode invokes mount effects twice; without this guard the mutation
  // fires twice and creates two refinement sessions (and burns two Gemini
  // calls). Keyed by image id rather than a bare boolean so a user who hops
  // between library images via repeated /creative-studio?... links still
  // gets fresh sessions.
  const handledRefineFromLibraryRef = useRef<string | null>(null);

  // Note: Generation polling is handled in GenerationPanel to centralize
  // the logic and avoid duplicate store updates

  // Handle "Edit in Studio" navigation from the Media Library — read the
  // mediaImageId from the URL, kick off a refinement session, then clear
  // the param so a refresh inside the session doesn't re-trigger.
  // Depend on the stable `mutateAsync` rather than the whole mutation
  // object (the latter gets a new wrapper each render in React Query and
  // would otherwise re-fire the effect every render until the param clears).
  const startRefinementAsync = startRefinementMutation.mutateAsync;
  useEffect(() => {
    // Same access predicate the gate below uses. Without this check the
    // effect can fire for a no-access user mid-render and trigger a
    // mutation that 403s — wasted call + an error toast for a feature
    // they can't reach anyway.
    if (!hasCreativeStudioAccess(memberUuid)) return;

    const mediaImageId = searchParams.get(REFINE_FROM_LIBRARY_PARAM);
    if (!mediaImageId) {
      // Param cleared (we removed it, or the user navigated). Reset the
      // guard so the SAME image id can be retried later via a fresh nav
      // — otherwise a user who hits "Edit in Studio" twice for the same
      // image after dismissing the first session would get no response.
      handledRefineFromLibraryRef.current = null;
      return;
    }
    if (handledRefineFromLibraryRef.current === mediaImageId) return;
    handledRefineFromLibraryRef.current = mediaImageId;

    // Strip the param SYNCHRONOUSLY — before the await — so React 18
    // StrictMode's mount→unmount→mount cycle doesn't fire the mutation
    // twice. The second mount creates a fresh useRef (so the in-instance
    // guard above is null again); reading the now-cleaned URL is what
    // makes it bail. Setting the param strip in the async `finally`
    // wasn't enough: in StrictMode the second mount runs before the
    // first mount's async completes, so it sees the still-present param.
    const next = new URLSearchParams(searchParams);
    next.delete(REFINE_FROM_LIBRARY_PARAM);
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        const session = await startRefinementAsync({
          media_image_id: mediaImageId,
        });
        startRefinementSession(session);
        setActiveTab('edit');
      } catch (err) {
        toast.error('Could not start a refinement session for that image.');
        console.error('refine_from_library failed:', err);
      }
    })();
  }, [
    memberUuid,
    searchParams,
    setSearchParams,
    startRefinementAsync,
    startRefinementSession,
    setActiveTab,
  ]);

  // Handle starting a refinement session
  const handleStartRefine = useCallback(async () => {
    if (!currentJobId) return;

    try {
      // Omit reference_images so the backend inherits the originating job's
      // refs (with the same user-supplied names) and truncates from 14 → 13
      // if needed. Sending the FE snapshot here would 422 when the user
      // generated with 14 refs because StartRefinementSessionRequest is
      // capped at 13 (Gemini's 14-image input cap minus 1 for the current
      // image at refinement time).
      const session = await startRefinementMutation.mutateAsync({
        generation_job_id: currentJobId,
        image_index: 0,
      });
      startRefinementSession(session);
      setActiveTab('edit');
    } catch (error) {
      console.error('Failed to start refinement session:', error);
    }
  }, [currentJobId, startRefinementMutation, startRefinementSession, setActiveTab]);

  // Access gate - should not be reachable if nav link is properly hidden,
  // but this provides defense in depth
  if (!hasCreativeStudioAccess(memberUuid)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
          <p className="text-gray-600">
            Creative Studio is not available for your account.
          </p>
        </div>
      </div>
    );
  }

  const refineTabDisabled = !refinementSessionId && !selectedImage;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Creative Studio</h1>
          <p className="mt-1 text-sm text-gray-600">
            Create and edit your ad content with AI-powered tools.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('generate')}
              className={`group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium
                ${
                  activeTab === 'generate'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
            >
              <AISparkleIcon
                className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === 'generate' ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              Generate
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              disabled={refineTabDisabled}
              className={`group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium
                ${
                  activeTab === 'edit'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }
                ${refineTabDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <PencilSquareIcon
                className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === 'edit' ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              Refine
              {refinementSessionId && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  Active
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Main Content - Image preview spans the full width on top, with
            the generation/refinement controls stacked beneath it. The image
            is the focal point so it gets the real estate; the controls
            condense into a wide row to fit underneath. */}
        <div className="space-y-6">
          <ImagePreview onStartRefine={handleStartRefine} />
          {activeTab === 'generate' ? <GenerationPanel /> : <RefinementPanel />}
        </div>

        {/* Beta Notice */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AISparkleIcon className="h-5 w-5 text-amber-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Beta Feature</h3>
              <div className="mt-1 text-sm text-amber-700">
                <p>
                  Creative Studio is in beta. Generation uses Gemini 3 Pro and may take 10-30 seconds. Feedback welcome!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Modal */}
      <SaveToMediaLibraryModal />
    </div>
  );
}
